// Formateo de plata, peso y fechas.
//
// Está centralizado porque los formatos son una CONVENCIÓN del sistema, no una
// decisión de cada pantalla: si cada componente formatea a su manera, la misma
// cifra aparece de dos formas distintas en dos lugares y el usuario deja de
// confiar en los números.

const ES = 'es-AR'

const plataFmt = new Intl.NumberFormat(ES, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const plataCortaFmt = new Intl.NumberFormat(ES, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/** $1.234,50 — siempre con dos decimales. */
export function plata(valor: number | null | undefined): string {
  if (valor == null || Number.isNaN(valor)) return '—'

  // El signo va ANTES del peso: «-$1.777,78» y no «$-1.777,78».
  //
  // Se pegaba el `$` adelante del número formateado, y el formateador pone el
  // menos primero, así que todo importe negativo salía con el signo en el
  // medio. Se ve en la diferencia de un arqueo que faltó, en el neto del cajón
  // de un turno con retiros y en cualquier ajuste de una cuenta corriente: son
  // justamente los números que alguien está mirando porque algo no cuadra, y
  // el que los lee tiene que poder confiar en cómo están escritos.
  const negativo = valor < 0

  return `${negativo ? '-' : ''}$${plataFmt.format(Math.abs(valor))}`
}

/**
 * Centavos a pesos: $1.234,50.
 *
 * Existe además de `plata` porque todo el POS y toda la caja trabajan en
 * CENTAVOS ENTEROS, y convertir a mano en cada componente es la forma de que
 * en algún lugar quede un precio cien veces más grande. La función lo hace en
 * un solo lugar y el nombre dice qué entra.
 */
export function plataCentavos(centavos: number | null | undefined): string {
  if (centavos == null || Number.isNaN(centavos)) return '—'
  return plata(centavos / 100)
}

/**
 * El importe que se está TIPEANDO, formateado sin corregir lo que se escribió.
 *
 * Es distinto de `plata` a propósito y por eso vive acá y no en la pantalla: lo
 * que se muestra tiene que ser LO QUE LA PERSONA TIPEÓ, o el display miente.
 * Con `plata`, apretar la coma no cambiaba nada visible —«$1.500,00» antes y
 * después— así que la tecla parecía no funcionar; y mientras se escribe el
 * primer centavo, «$1.500,5» no es «$1.500,50»: son cinco pesos de diferencia y
 * el que está mirando tiene que poder ver en cuál de los dos está.
 *
 * Entra el texto crudo del teclado del POS («1500», «1500,», «1500,5») y sale
 * con el símbolo y los puntos de miles. La parte entera la formatea `Intl`,
 * que es lo que hace que esta función tenga que estar en este archivo: es el
 * único lugar del frontend que puede.
 */
export function plataTipeada(tipeado: string): string {
  if (tipeado === '' || tipeado === ',') return plata(0)

  const coma = tipeado.indexOf(',')

  if (coma === -1) return plata(Number(tipeado || '0'))

  const enteros = Number(tipeado.slice(0, coma) || '0')
  const decimales = tipeado.slice(coma + 1)

  return `$${plataCortaFmt.format(enteros)},${decimales}`
}

/**
 * Cantidad en milésimas al texto con los decimales de la unidad.
 * 1250 milésimas con 3 decimales es «1,250»; con 0 decimales, «2».
 */
export function cantidadMilesimas(
  milesimas: number | null | undefined,
  decimales = 3,
): string {
  if (milesimas == null || Number.isNaN(milesimas)) return '—'
  return cantidad(milesimas / 1000, decimales)
}

/**
 * $1.235 — sin centavos, para las cifras grandes de los tableros.
 * No se usa en tickets ni en el arqueo: ahí los centavos importan.
 */
/**
 * El tamaño de un archivo, en la unidad que una persona entiende.
 *
 * Vive acá y no en las dos pantallas que lo muestran por la razón de siempre:
 * escrito dos veces, una dice «0 MB» para un archivo de dos kilobytes y la
 * otra «0.0 MB», y cuál se ve depende de por dónde entraste.
 *
 * Debajo de un mega va en KB: un instalador de verdad son decenas de megas,
 * pero uno de prueba —o uno que subió cortado— se lee «0 MB», que parece un
 * archivo vacío y no lo es.
 */
export function tamanoArchivo(bytes: number | null | undefined): string {
  const n = bytes ?? 0

  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`

  return `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

export function plataCorta(valor: number | null | undefined): string {
  if (valor == null || Number.isNaN(valor)) return '—'

  // El signo antes del peso, por lo mismo que en `plata`.
  const negativo = valor < 0

  return `${negativo ? '-' : ''}$${plataCortaFmt.format(Math.abs(valor))}`
}

/**
 * Peso con la cantidad de decimales que declara la unidad.
 * 2,840 kg con 3 decimales es el gramo exacto; 3 unidades no lleva decimales.
 */
export function cantidad(valor: number | null | undefined, decimales = 3): string {
  if (valor == null || Number.isNaN(valor)) return '—'
  return new Intl.NumberFormat(ES, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(valor)
}

/** dd/mm/yyyy. Nunca ISO ni 26/1/27. */
export function fecha(valor: string | Date | null | undefined): string {
  if (!valor) return '—'

  // Una fecha de CALENDARIO no es un instante, y por eso no se le convierte la
  // zona a nada. «2026-09-01» pasado por `new Date` queda a medianoche UTC, y
  // formateado en la zona del local —que está al oeste— muestra el día
  // ANTERIOR: la promoción que arranca el 1° de septiembre decía «31/08/2026»
  // en la grilla, con la base diciendo 2026-09-01. Es la otra cara del gotcha
  // que ya se pagó en los reportes, donde la salida fue mandar `DateOnly`: el
  // servidor lo hizo bien y el navegador lo volvía a correr.
  //
  // Lo resuelve el formateador y no cada pantalla porque el que llama no tiene
  // por qué saber si el campo era `DateOnly` o `DateTime`: el que sabe es el
  // dato, y se reconoce por la forma.
  if (typeof valor === 'string') {
    const calendario = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor)
    if (calendario) return `${calendario[3]}/${calendario[2]}/${calendario[1]}`
  }

  const d = typeof valor === 'string' ? new Date(valor) : valor
  if (Number.isNaN(d.getTime())) return '—'

  return fechaFmt.format(d)
}

// ── La zona horaria del LOCAL ────────────────────────────────────────────────
//
// Todo viaja en UTC (§1.8) y se presenta en la zona del local, que NO es la
// del navegador. Sin decirle la zona a Intl, la hora que se ve depende de cómo
// esté configurada la máquina: en la PC del mostrador con la zona en UTC —que
// vienen así de fábrica y nadie las toca— la grilla mostraba 18:36 para la
// misma venta cuyo ticket impreso dice 15:36, y el reporte del día, que el
// servidor agrupa por la hora del local, no cerraba contra ninguna de las dos.
//
// La zona sale del tenant de la sesión y se fija al entrar (ver lib/sesion).
// Mientras no se sepa, se usa la del navegador: es lo mejor que hay antes de
// que alguien se autentique, y en la práctica es la misma.
let zonaDelLocalActual: string | undefined

let fechaFmt: Intl.DateTimeFormat
let horaFmt: Intl.DateTimeFormat

function armarFormatos() {
  const conZona = <T extends Intl.DateTimeFormatOptions>(opciones: T) => {
    try {
      return new Intl.DateTimeFormat(ES, { ...opciones, timeZone: zonaDelLocalActual })
    } catch {
      // Una zona que este navegador no conoce no puede dejar la pantalla sin
      // fechas: se cae a la del navegador.
      return new Intl.DateTimeFormat(ES, opciones)
    }
  }

  fechaFmt = conZona({ day: '2-digit', month: '2-digit', year: 'numeric' })

  // La hora va en 24 horas, siempre.
  //
  // `hour12: false` es explícito porque el default de es-AR es el reloj de 12
  // y devuelve «08:50 a. m.»: ocupa el doble en una celda de grilla, y encima
  // el ticket que sale por la térmica imprime «08:50». La misma venta se leía
  // de dos formas distintas según dónde se la mirara.
  horaFmt = conZona({ hour: '2-digit', minute: '2-digit', hour12: false })
}

armarFormatos()

/**
 * Fija la zona horaria del local. La llama la sesión al entrar.
 *
 * Pasar `null` vuelve a la del navegador, que es lo que corresponde cuando no
 * se sabe de qué local es la pantalla.
 */
export function zonaDelLocal(): string | undefined {
  return zonaDelLocalActual
}

export function fijarZonaDelLocal(nombre: string | null | undefined) {
  const zona = nombre?.trim() ?? ''

  if ((zona || undefined) === zonaDelLocalActual) return

  zonaDelLocalActual = zona || undefined
  armarFormatos()
}

/** dd/mm/yyyy HH:mm */
export function fechaHora(valor: string | Date | null | undefined): string {
  if (!valor) return '—'
  const d = typeof valor === 'string' ? new Date(valor) : valor
  if (Number.isNaN(d.getTime())) return '—'

  return `${fecha(d)} ${horaFmt.format(d)}`
}

/** Solo la hora: para el detalle de un turno, donde el día ya es el contexto. */
export function hora(valor: string | Date | null | undefined): string {
  if (!valor) return '—'
  const d = typeof valor === 'string' ? new Date(valor) : valor
  if (Number.isNaN(d.getTime())) return '—'

  return horaFmt.format(d)
}

/** "hace 3 min", para indicadores en vivo. */
export function haceCuanto(valor: string | Date | null | undefined): string {
  if (!valor) return '—'
  const d = typeof valor === 'string' ? new Date(valor) : valor
  if (Number.isNaN(d.getTime())) return '—'

  const segundos = Math.floor((Date.now() - d.getTime()) / 1000)

  if (segundos < 60) return 'hace instantes'
  if (segundos < 3600) return `hace ${Math.floor(segundos / 60)} min`
  if (segundos < 86400) return `hace ${Math.floor(segundos / 3600)} h`

  return fecha(d)
}

/** 12,5% con signo, para variaciones. */
export function porcentaje(valor: number | null | undefined, conSigno = false): string {
  if (valor == null || Number.isNaN(valor)) return '—'

  const n = new Intl.NumberFormat(ES, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(valor)

  return `${conSigno && valor > 0 ? '+' : ''}${n}%`
}

/**
 * Normaliza para buscar: sin acentos y en minúsculas.
 * Lo usa el filtrado de los combos del lado del cliente, para que "rucula"
 * encuentre "Rúcula" — nadie tipea los acentos con las manos ocupadas.
 */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

/**
 * Texto tipeado a número, aceptando lo que la gente escribe de verdad.
 *
 * En una planilla de precios nadie tipea "1234.5": tipea "1.234,50" porque es
 * como lo ve en pantalla, o "1234,5", o pega una celda de Excel que ya viene
 * con separador de miles. Un `Number()` pelado devuelve NaN en el primer caso
 * y 1.234 en el segundo — un precio mil veces más chico, guardado sin que
 * nadie se entere.
 *
 * Devuelve null cuando no hay un número reconocible, para que quien llama
 * decida si eso es un error o un campo vacío.
 */
export function aNumero(texto: string | null | undefined): number | null {
  if (texto == null) return null

  let t = texto.trim().replace(/\s/g, '').replace(/^\$/, '')
  if (t === '') return null

  const tieneComa = t.includes(',')
  const tienePunto = t.includes('.')

  if (tieneComa && tienePunto) {
    // Los dos separadores: el ÚLTIMO que aparece es el decimal y el otro es
    // el de miles. Resuelve "1.234,50" y también "1,234.50", que es lo que
    // sale de un Excel en inglés.
    t =
      t.lastIndexOf(',') > t.lastIndexOf('.')
        ? t.replace(/\./g, '').replace(',', '.')
        : t.replace(/,/g, '')
  } else if (tieneComa) {
    t = t.replace(',', '.')
  } else if (tienePunto) {
    // Un solo punto y exactamente tres dígitos detrás es separador de miles:
    // "1.500" son mil quinientos pesos, no un peso con cincuenta. Con otra
    // cantidad de decimales ("1.5", "1.50") es decimal.
    const partes = t.split('.')
    if (partes.length > 2 || partes[1]?.length === 3) t = t.replace(/\./g, '')
  }

  const n = Number(t)
  return Number.isFinite(n) ? n : null
}
